import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

function EditWorkOrderModal({ workOrder, onClose, onSave }) {
  const [name, setName] = useState(workOrder.name);
  const [priority, setPriority] = useState(workOrder.priority);
  const [dueDate, setDueDate] = useState(workOrder.dueDate);
  const [tasks, setTasks] = useState(workOrder.tasks);

  // Handle task field updates
  const handleTaskChange = (index, field, value) => {
    const updatedTasks = [...tasks];
    updatedTasks[index][field] = value;

    // Automatically block tasks if their dependency is incomplete
    if (field === "dependency" && value) {
      const dependentTask = updatedTasks.find((task) => task.id === parseInt(value, 10));
      if (dependentTask && dependentTask.status !== "Completed") {
        updatedTasks[index].status = "Blocked";
      }
    }

    setTasks(updatedTasks);
  };

  // Handle drag and drop for reordering tasks
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedTasks = Array.from(tasks);
    const [removed] = reorderedTasks.splice(result.source.index, 1);
    reorderedTasks.splice(result.destination.index, 0, removed);

    setTasks(reorderedTasks);
  };

  // Save the updated work order
  const handleSave = () => {
    if (!name.trim()) {
      alert("Work order name is required.");
      return;
    }

    onSave({
      ...workOrder,
      name,
      priority,
      dueDate,
      tasks,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white w-full max-w-3xl h-[calc(100vh-4rem)] rounded-md shadow-lg flex flex-col">
        {/* Header */}
        <div className="sticky top-16 bg-gray-100 border-b p-4 z-10">
          <h2 className="text-xl font-bold text-gray-800">Edit Work Order</h2>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Work Order Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Work Order Name</label>
            <input
              type="text"
              className="w-full p-2 mt-1 border rounded"
              placeholder="Work Order Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Priority</label>
              <select
                className="w-full p-2 mt-1 border rounded"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Due Date</label>
              <input
                type="date"
                className="w-full p-2 mt-1 border rounded"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          {/* Tasks Section */}
          <h3 className="text-lg font-bold text-gray-800">Tasks</h3>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="tasks">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-4"
                >
                  {tasks.map((task, index) => (
                    <Draggable
                      key={task.id}
                      draggableId={task.id.toString()}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                          className="p-4 border rounded bg-gray-50"
                        >
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Task Name
                            </label>
                            <input
                              type="text"
                              className="w-full p-2 mt-1 border rounded"
                              placeholder="Task Name"
                              value={task.name}
                              onChange={(e) =>
                                handleTaskChange(index, "name", e.target.value)
                              }
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4 mt-2">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Dependency
                              </label>
                              <select
                                className="w-full p-2 mt-1 border rounded"
                                value={task.dependency || ""}
                                onChange={(e) =>
                                  handleTaskChange(index, "dependency", e.target.value)
                                }
                              >
                                <option value="">None</option>
                                {tasks
                                  .filter((t) => t.id !== task.id)
                                  .map((t) => (
                                    <option key={t.id} value={t.id}>
                                      {t.name}
                                    </option>
                                  ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Status
                              </label>
                              <select
                                className="w-full p-2 mt-1 border rounded"
                                value={task.status}
                                onChange={(e) =>
                                  handleTaskChange(index, "status", e.target.value)
                                }
                                disabled={task.status === "Blocked"}
                              >
                                <option value="Pending">Pending</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Completed">Completed</option>
                                <option value="Blocked">Blocked</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-100 border-t p-4 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditWorkOrderModal;